import type { Locale } from './locales'

/**
 * Place names in the reader's locale.
 *
 * A **country** name is universal CLDR data, not editorial vocabulary
 * (→ invariants.md#facts-canonical-store-editorial): the warehouse stores the ISO code
 * and the platform supplies the name, so no translator maintains a list of countries per
 * locale and no locale can silently fall back to English. Same rationale as the calendar
 * names in `calendar.ts`; **subdivision** names (US states, Canadian provinces) are
 * deliberately absent, because CLDR's subdivision data is not exposed through `Intl` —
 * those stay canonical.
 *
 * Total, like the calendar helpers: returns `null` rather than throwing, because these
 * fill a rendered label and an unresolvable code should drop its rung, not the page.
 */

/** One `Intl.DisplayNames` per locale, built once — the constructor is the expensive part
 *  and every C2C page resolves a country name. Bounded by the served-locale set. */
const regionNames = new Map<Locale, Intl.DisplayNames>()

/** An ISO 3166-1 alpha-2 country code as the locale's country name; `null` if it is not one. */
export function countryName(code: string | undefined, locale: Locale): string | null {
    if (!code || !/^[A-Za-z]{2}$/.test(code)) {
        return null
    }
    let display = regionNames.get(locale)
    if (!display) {
        display = new Intl.DisplayNames([locale], { type: 'region' })
        regionNames.set(locale, display)
    }
    const upper = code.toUpperCase()
    // `of` echoes the input back when the code has no CLDR entry — that is not a name.
    const resolved = display.of(upper)
    return resolved && resolved !== upper ? resolved : null
}
